"use client";

import { useState, useEffect } from "react";
import { api } from "@/lib/firebase/api-client";
import { UserRole } from "@/lib/constants";
import { envoyerLienMotDePasse } from "@/lib/firebase/auth";
import { useAuth } from "@/lib/hooks/useAuth";
import { useStores } from "@/lib/hooks/queries";
import { Headphones, Plus, Edit, Trash2, Eye, EyeOff, X } from "lucide-react";

interface SupportUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  // Le type doit connaitre le champ, sinon TypeScript refuse de compiler
  // des qu'on le lit — meme si la donnee existe bien cote serveur.
  phone?: string;
  // Magasin de rattachement. Vide = couvre tous les magasins.
  storeId?: string;
  role: UserRole;
  isActive: boolean;
  /** Gestion des fournisseurs ouverte a ce compte (18/10/2026). */
  accesFournisseurs?: boolean;
  createdAt: any;
  updatedAt: any;
}

export default function SupportsPage() {
  const { user, loading: authLoading } = useAuth();
  const { data: stores = [] } = useStores();
  const [supports, setSupports] = useState<SupportUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSupport, setEditingSupport] = useState<SupportUser | null>(
    null,
  );
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    storeId: "",
    role: UserRole.SUPPORT as UserRole,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Attendre que l'auth soit prête et que l'utilisateur soit connecté
    if (!authLoading && user) {
      loadSupports();
    }
  }, [authLoading, user]);

  const loadSupports = async () => {
    try {
      setLoading(true);
      const result = await api.users.getAll();
      // Cette page gere les deux roles qui n'ont pas d'ecran ailleurs :
      // SUPPORT (l'equipe) et LECTEUR (les agents automatiques, en
      // consultation seule). Sans ca, un compte LECTEUR devient invisible.
      const supportUsers = result.filter(
        (u: any) =>
          u.role === UserRole.SUPPORT || u.role === UserRole.LECTEUR,
      );
      setSupports(supportUsers);
    } catch (error: any) {
      console.error("Error loading supports:", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (support?: SupportUser) => {
    if (support) {
      setEditingSupport(support);
      setFormData({
        username: support.username,
        password: "",
        email: support.email || "",
        firstName: support.firstName || "",
        lastName: support.lastName || "",
        phone: support.phone || "",
        storeId: support.storeId || "",
        role: support.role || UserRole.SUPPORT,
      });
    } else {
      setEditingSupport(null);
      setFormData({
        username: "",
        password: "",
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
        storeId: "",
        role: UserRole.SUPPORT,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSupport(null);
    setFormData({
      username: "",
      password: "",
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      storeId: "",
      role: UserRole.SUPPORT,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Un humain a besoin d'une adresse — c'est par la qu'arrive son lien.
    // Un agent automatique a besoin d'un mot de passe — il ne lira aucun
    // e-mail. Les deux cas sont exclusifs, et l'ecran doit le dire avant
    // d'envoyer quoi que ce soit au serveur.
    const estAgent = formData.role === UserRole.LECTEUR;

    if (!formData.username) {
      alert("Le nom d'utilisateur est obligatoire.");
      return;
    }
    if (!editingSupport && !estAgent && !formData.email) {
      alert(
        "L'adresse e-mail est obligatoire : c'est par là que la personne recevra son lien pour choisir son mot de passe.",
      );
      return;
    }
    if (!editingSupport && estAgent && !formData.password) {
      alert(
        "Un compte Lecteur ne reçoit pas d'e-mail : donne-lui un mot de passe.",
      );
      return;
    }

    setSubmitting(true);
    try {
      if (editingSupport) {
        // Modification
        const updateData: any = {
          username: formData.username,
          email: formData.email || undefined,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          phone: formData.phone || undefined,
          storeId: formData.storeId || "",
          role: formData.role,
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        await api.users.update(editingSupport.id, updateData);
        alert("✅ Compte modifié avec succès");
      } else {
        // Création
        await api.users.create({
          username: formData.username,
          // Vide pour un humain : le serveur en tire un au hasard que
          // personne ne connaitra, et la personne choisira le sien.
          password: formData.password || undefined,
          email: formData.email || undefined,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          phone: formData.phone || undefined,
          storeId: formData.storeId || "",
          role: formData.role,
        });

        if (!estAgent && formData.email) {
          await envoyerLienMotDePasse(formData.email);
          alert(
            `✅ Compte créé.\n\nUn e-mail vient d'être envoyé à ${formData.email}. ` +
              `La personne y choisit son mot de passe, puis se connecte avec son nom d'utilisateur.\n\n` +
              `Pense à lui dire de regarder ses spams.`,
          );
        } else {
          alert("✅ Compte créé avec le mot de passe que tu as fixé.");
        }
      }

      handleCloseModal();
      loadSupports();
    } catch (error: any) {
      console.error("Error saving support:", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  /** Ouvrir ou fermer la page Fournisseurs a CE compte support. */
  const handleToggleFournisseurs = async (support: SupportUser) => {
    const ouvrir = !support.accesFournisseurs;
    if (
      !confirm(
        ouvrir
          ? `Donner à ${support.username} l'accès à la gestion des fournisseurs (créer, modifier — pas supprimer) ?`
          : `Retirer à ${support.username} l'accès à la gestion des fournisseurs ?`,
      )
    ) {
      return;
    }
    try {
      await api.users.update(support.id, { accesFournisseurs: ouvrir } as any);
      loadSupports();
    } catch (error: any) {
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleToggleActive = async (support: SupportUser) => {
    if (
      !confirm(
        `${support.isActive ? "Désactiver" : "Activer"} ${support.username} ?`,
      )
    ) {
      return;
    }

    try {
      await api.users.update(support.id, { isActive: !support.isActive });
      loadSupports();
    } catch (error: any) {
      console.error("Error toggling support:", error);
      alert(`Erreur: ${error.message}`);
    }
  };

  const handleDelete = async (support: SupportUser) => {
    if (!confirm(`Supprimer définitivement ${support.username} ?`)) {
      return;
    }

    try {
      await api.users.delete(support.id);
      alert("✅ Support supprimé");
      loadSupports();
    } catch (error: any) {
      console.error("Error deleting support:", error);
      alert(`Erreur: ${error.message}`);
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Headphones className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              Supports et lecteurs
            </h1>
            <p className="text-sm text-gray-600">
              Comptes de l'équipe support et comptes en consultation seule
            </p>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden md:inline">Créer un compte</span>
          <span className="md:hidden">Créer</span>
        </button>
      </div>

      {/* Liste des supports */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      ) : supports.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <Headphones className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-600 mb-4">Aucun compte support ni lecteur</p>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Créer le premier compte
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                  Utilisateur
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase hidden lg:table-cell">
                  Nom complet
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Rôle
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                  Statut
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {supports.map((support) => (
                <tr key={support.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Headphones className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {support.username}
                        </p>
                        <p className="text-xs text-gray-500 md:hidden">
                          {support.email || "Pas d'email"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell">
                    {support.email || "-"}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600 hidden lg:table-cell">
                    {support.firstName || support.lastName
                      ? `${support.firstName || ""} ${support.lastName || ""}`.trim()
                      : "-"}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {/* Le bleu dit "consultation", le violet dit "peut agir".
                        La couleur porte l'information, pas la decoration. */}
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        support.role === UserRole.LECTEUR
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                      title={
                        support.role === UserRole.LECTEUR
                          ? "Consultation seule — ne peut rien modifier"
                          : "Support — peut modifier les commandes et écrire aux clients"
                      }
                    >
                      {support.role === UserRole.LECTEUR ? "Lecteur" : "Support"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        support.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {support.isActive ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenModal(support)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      {support.role === UserRole.SUPPORT && (
                        <button
                          onClick={() => handleToggleFournisseurs(support)}
                          className={`px-2 py-1 rounded-lg text-xs font-semibold border ${
                            support.accesFournisseurs
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "text-gray-500 border-gray-300 hover:bg-gray-50"
                          }`}
                          title="Accès à la page Fournisseurs (créer, modifier — pas supprimer)"
                        >
                          Fournisseurs : {support.accesFournisseurs ? "oui" : "non"}
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(support)}
                        className={`p-2 rounded-lg ${
                          support.isActive
                            ? "text-orange-600 hover:bg-orange-50"
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={support.isActive ? "Désactiver" : "Activer"}
                      >
                        {support.isActive ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(support)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Créer/Modifier */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">
                {editingSupport ? "Modifier le compte" : "Créer un compte"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="support01"
                  required
                />
              </div>

              {/* Mot de passe — uniquement pour les comptes techniques.
                  Un humain n'en saisit pas : il choisira le sien. */}
              {(formData.role === UserRole.LECTEUR || editingSupport) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mot de passe{" "}
                    {formData.role === UserRole.LECTEUR && !editingSupport && (
                      <span className="text-red-500">*</span>
                    )}
                    {editingSupport && (
                      <span className="text-xs text-gray-500">
                        (laisser vide pour ne pas changer)
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                  <p className="mt-1.5 text-xs text-gray-500">
                    Un agent automatique ne lit pas ses e-mails : il lui faut
                    un mot de passe fixé ici.
                  </p>
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email{" "}
                  {formData.role !== UserRole.LECTEUR ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-xs text-gray-500">(optionnel)</span>
                  )}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="prenom@exemple.com"
                  required={formData.role !== UserRole.LECTEUR}
                />
                {formData.role !== UserRole.LECTEUR && !editingSupport && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    La personne recevra un e-mail pour choisir son mot de
                    passe. Tu ne le connaîtras pas — c'est voulu.
                  </p>
                )}
              </div>

              {/* Magasin — seulement pour un humain.
                  Un compte Lecteur est un agent automatique : il ne lit
                  aucune conversation, la question du perimetre ne se pose
                  pas pour lui. */}
              {formData.role !== UserRole.LECTEUR && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Magasin
                  </label>
                  <select
                    value={formData.storeId}
                    onChange={(e) =>
                      setFormData({ ...formData, storeId: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Tous les magasins</option>
                    {stores.map((s: any) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500">
                    Détermine les conversations fournisseurs visibles par cette
                    personne. « Tous les magasins » donne accès aux deux.
                  </p>
                </div>
              )}

              {/* Téléphone WhatsApp */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone WhatsApp{" "}
                  <span className="text-xs text-gray-500">(optionnel)</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="0777243624 ou +212777243624"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Sans ce numéro, les échanges WhatsApp de cette personne
                  n'apparaîtront pas dans la messagerie : rien ne les
                  relierait à son profil.
                </p>
              </div>

              {/* Prénom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom{" "}
                  <span className="text-xs text-gray-500">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Ahmed"
                />
              </div>

              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-xs text-gray-500">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Benali"
                />
              </div>

              {/* Rôle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value as UserRole,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value={UserRole.SUPPORT}>
                    Support — peut modifier les commandes et écrire aux clients
                  </option>
                  <option value={UserRole.LECTEUR}>
                    Lecteur — consultation seule, ne peut rien modifier
                  </option>
                </select>
                <p className="mt-1.5 text-xs text-gray-500">
                  Choisissez Lecteur pour un agent automatique : il pourra tout
                  consulter, mais l'API refusera toute écriture.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  disabled={submitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? "..." : editingSupport ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
